import type { PropsWithChildren } from "react";
import { useContext } from "react";
import { useSelector } from "react-redux";
import {
  getDeviceType as getCoreDeviceType,
  isWebView,
} from "@b2bportal/core-utilities";
import {
  AxiosInterceptors,
  createRequestLog,
  createResponseErrorLog,
  createResponseLog,
} from "@hopper-b2b/api";
import { useExperiments } from "@hopper-b2b/experiments";
import { useI18nContext } from "@hopper-b2b/i18n";
import { getDelegatedTo } from "@hopper-b2b/self-serve";
import {
  USER_CAMPAIGN_KEY,
  USER_CONTENT_KEY,
  USER_MEDIUM_KEY,
  USER_SOURCE_KEY,
  UserAgentDeviceTypeEnum,
} from "@hopper-b2b/utilities";
import { UserContext } from "@/App";
import { AppTrackingContext } from "@/components/AppTrackingContext";

function getRbcDeviceType(): string {
  if (isWebView()) return UserAgentDeviceTypeEnum.MobileApp;
  const d = getCoreDeviceType();
  if (d.isMobile) return UserAgentDeviceTypeEnum.Mobile;
  if (d.isTablet) return UserAgentDeviceTypeEnum.Tablet;
  return UserAgentDeviceTypeEnum.Desktop;
}

export interface IAxiosInterceptors {
  version: string;
}

export const AxiosInterceptorWrapper = ({
  children,
  version,
}: PropsWithChildren<IAxiosInterceptors>) => {
  const { trackingProperties } = useExperiments();
  const { trackers } = useContext(AppTrackingContext);
  const sessionId = sessionStorage.getItem("sessionId");
  const { locale, brand } = useI18nContext();
  const currency = brand.currency;
  const tenant = brand.clientName;
  const headers = {};
  if (sessionId) headers["Riskified-Session-Id"] = sessionId;
  const delegatedTo = useSelector(getDelegatedTo);

  const { sessionInfo } = useContext(UserContext);
  const partnerMetadata = sessionInfo?.userInfo?.partnerMetadata;
  const userTier = partnerMetadata?.tierEn?.toLowerCase();
  const userProvince = partnerMetadata?.provinceCode;
  const accountName = partnerMetadata?.accountNameEn;

  const additionalTrackingProperties = {
    ...(userTier ? { user_tier: userTier } : {}),
    ...(userProvince ? { user_province: userProvince } : {}),
    ...(accountName ? { accountName } : {}),
  };

  return (
    <AxiosInterceptors
      trackingProperties={trackingProperties}
      additionalTrackingProperties={additionalTrackingProperties}
      userSource={trackers[USER_SOURCE_KEY]}
      userMedium={trackers[USER_MEDIUM_KEY]}
      userCampaign={trackers[USER_CAMPAIGN_KEY]}
      userContent={trackers[USER_CONTENT_KEY]}
      tenant={tenant}
      locale={locale}
      currency={currency.code}
      requestHeaders={headers}
      version={version}
      logResponse={createResponseLog}
      logRequest={createRequestLog}
      logError={createResponseErrorLog}
      delegatedTo={delegatedTo}
      deviceType={getRbcDeviceType()}
    >
      {children}
    </AxiosInterceptors>
  );
};
